<script lang="ts" setup>
import { useWindowScroll } from '@vueuse/core'
import { useTranslation } from 'i18next-vue'
import { Button, Menubar } from 'primevue'
import type { MenuItem } from 'primevue/menuitem'
import { computed } from 'vue'

import LanguageSelector from '@/components/common/LanguageSelector.vue'
import PvIcon from '@/components/ui/PvIcon.vue'
import { headerNav, type NavItem } from '@/constants/navigation'

const { y } = useWindowScroll()
const { t } = useTranslation()

const toMenuItem = (item: NavItem): MenuItem => ({
  label: t(item.labelKey),
  route: item.to,
  items: item.children?.map(toMenuItem),
})

const items = computed<MenuItem[]>(() => headerNav.map(toMenuItem))
</script>

<template>
  <header class="site-topbar">
    <nav :aria-label="$t('common:primaryNavigation')">
      <Menubar
        :class="{ 'shadow-xl': y > 24 }"
        :model="items"
        class="site-topbar-surface border-none"
      >
        <template #start>
          <RouterLink class="shrink-0 px-2" to="/">
            <img alt="Pin vision" src="../../assets/brand/logo.svg?link" width="171" height="50" />
          </RouterLink>
        </template>

        <template #item="{ item, props, hasSubmenu, root }">
          <RouterLink v-if="item.route" v-slot="{ href, navigate }" :to="item.route" custom>
            <a
              :class="{ 'nav-active': item.route === $route.path }"
              :href="href"
              class="nav-item"
              v-bind="props.action"
              @click="navigate"
            >
              {{ item.label }}
            </a>
          </RouterLink>
          <button v-else :class="{ 'nav-item': root }" type="button" v-bind="props.action">
            {{ item.label }}
            <PvIcon
              v-if="hasSubmenu"
              name="chevron-down"
              class="w-4 h-4 chevron-icon transition-transform"
            />
          </button>
        </template>

        <template #end>
          <section class="flex gap-4 items-center">
            <LanguageSelector />
            <Button as="router-link" class="p-button-website" to="/admin" outlined>
              {{ $t('common:headerLogin') }}
            </Button>

            <Button as="router-link" class="p-button-website" secondary to="/app">
              {{ $t('common:headerDownload') }}
            </Button>
          </section>
        </template>
      </Menubar>
    </nav>
  </header>
</template>

<style lang="scss" scoped>
@reference '../../assets/styles/root.scss';
:deep(.p-menubar-item-link) {
  @apply font-semibold;
}

:deep(.p-menubar-submenu) {
  @apply rounded-2xl border border-surface-200 bg-surface-0 shadow-xl p-2 mt-2 min-w-[200px];
}

:deep(.p-menubar-item-content) {
  @apply rounded-lg;
}

.nav-item {
  @apply font-semibold rounded-lg transition-all;
}

.nav-active {
  @apply bg-surface-100;
}

:deep(.p-menubar-item-active) .chevron-icon {
  transform: rotate(180deg);
}
</style>
